/**
 * §P.2 · El calendario: las clases de cada día.
 */

import { fechaParaInput, fecha } from '@/shared/ui/formato';

export type EstadoDeClase = 'scheduled' | 'held' | 'cancelled';

export interface Clase {
  readonly id: string;
  readonly group_id: string;
  readonly group_name: string | null;
  readonly venue_id: string | null;
  readonly session_date: Date | string;
  readonly starts_at: Date | string;
  readonly ends_at: Date | string;
  readonly status: string;
  readonly cancel_reason: string | null;
  readonly taught_by: string | null;
  /**
   * Si ya se pasó lista.
   *
   * `BFF_BO.md` §7.5 la llama **«la métrica madre del producto»**: «si vais a
   * destacar un número en el panel del club, que sea este». Un club que deja de
   * pasar lista se da de baja en unos 60 días, así que este dato predice las bajas
   * antes de que nadie las diga.
   */
  readonly roll_called_at: Date | string | null;
  readonly notes: string | null;
}

export interface PaginaDeClases {
  readonly data: readonly Clase[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/** Lo que devuelve el generador. **`skipped` no es un error.** */
export interface Generacion {
  readonly from: string;
  readonly to: string;
  readonly created: number;
  readonly skipped: number;
}

/** Máximo del contrato: «un calendario generado a un año vista es un calendario
 * que nadie ha revisado». */
export const MAXIMO_DE_DIAS = 62;

/** `yyyy-mm-dd` en local. **No `toISOString()`**: recortarlo saca el día anterior
 * al oeste de Greenwich. */
export const comoDia = (d: Date): string => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

/** El primer y el último día de un mes, a partir de `yyyy-mm`. */
export function mesCompleto(mes: string): { from: string; to: string } {
  const [anio, m] = mes.split('-').map(Number);
  const primero = new Date(anio!, m! - 1, 1);
  const ultimo = new Date(anio!, m!, 0);
  return { from: comoDia(primero), to: comoDia(ultimo) };
}

/** `yyyy-mm` del mes en curso. */
export const mesActual = (hoy = new Date()): string =>
  `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;

/** Mueve un mes hacia delante o hacia atrás, sin desbordar el año. */
export function mesDesplazado(mes: string, pasos: number): string {
  const [anio, m] = mes.split('-').map(Number);
  const d = new Date(anio!, m! - 1 + pasos, 1);
  return mesActual(d);
}

export const nombreDeMes = (mes: string): string => {
  const [anio, m] = mes.split('-').map(Number);
  return new Intl.DateTimeFormat('es-ES', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(anio!, m! - 1, 1));
};

/** `HH:MM` de un instante, en la zona de quien mira. */
export const hora = (valor: Date | string): string =>
  new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(valor instanceof Date ? valor : new Date(valor));

/**
 * Agrupa las clases por día **conservando el orden que vino del servidor**.
 *
 * «Ordenadas hacia adelante, al contrario que el resto de listados: un calendario
 * se lee del futuro al pasado, y quien lo abre está mirando la semana que viene.»
 * **No las reordenéis**, dice el BFF, así que esto agrupa y no ordena.
 */
export function porDia(
  clases: readonly Clase[]
): readonly { readonly dia: string; readonly clases: readonly Clase[] }[] {
  const dias: { dia: string; clases: Clase[] }[] = [];

  for (const c of clases) {
    const d =
      c.session_date instanceof Date
        ? comoDia(c.session_date)
        : String(c.session_date).slice(0, 10);

    const ultimo = dias[dias.length - 1];
    if (ultimo && ultimo.dia === d) ultimo.clases.push(c);
    else dias.push({ dia: d, clases: [c] });
  }

  return dias;
}

/**
 * §P.2 · Un aviso de ausencia de una familia.
 *
 * **Dos formas de avisar y las dos son legítimas.** `session_id` es «este martes no
 * va»; `absent_from`/`absent_to` es «del 3 al 17, que nos vamos». Obligar a partir un
 * viaje en siete avisos sería pedirle a un padre que haga el trabajo del sistema.
 */
export interface AvisoDeAusencia {
  readonly id: string;
  readonly student_id: string;
  readonly student_name: string;
  /** La clase concreta, cuando el aviso es de una. */
  readonly session_id: string | null;
  readonly session_date: Date | string | null;
  readonly group_name: string | null;
  /** El tramo, cuando el aviso es de varios días. */
  readonly absent_from: Date | string | null;
  readonly absent_to: Date | string | null;
  readonly reason: string | null;
  readonly notified_by: string | null;
  readonly notified_by_name: string | null;
  readonly created_at: Date | string;
}

export interface PaginaDeAusencias {
  readonly data: readonly AvisoDeAusencia[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/**
 * Si el aviso cubre **varios días** en vez de una clase suelta.
 *
 * Se mira `absent_from` y no `session_id`, porque un aviso de tramo puede traer
 * sesión —la primera que pilla— y entonces mirar `session_id` lo contaría como
 * puntual: un viaje de dos semanas quedaría como «el martes no viene».
 */
export const esTramo = (a: AvisoDeAusencia): boolean => a.absent_from !== null;

/**
 * El aviso en una frase.
 *
 * Los tres casos se dicen distinto, y el tercero existe de verdad: un tramo **sin
 * fecha de vuelta** es «desde el 3, sin fecha», que es lo que escribe quien no sabe
 * cuánto va a durar una lesión. Pintarlo como «del 3 al —» no es una frase.
 */
export function descripcionDeAusencia(a: AvisoDeAusencia): string {
  if (a.absent_from === null) {
    return a.session_date
      ? `No viene a la clase del ${fecha(a.session_date)}`
      : 'No viene a una clase';
  }

  if (a.absent_to === null) {
    return `No viene desde el ${fecha(a.absent_from)}, sin fecha de vuelta`;
  }

  /*
   * `fechaParaInput` normaliza los dos lados a `YYYY-MM-DD`, vengan como `Date` o
   * como cadena. Antes era `String(x).slice(0, 10)`, que sobre un `Date` da
   * «Thu Sep 10» y **acertaba por accidente**: compara bien porque el día lleva cero
   * delante, pero se come el año, así que el mismo día de la semana y del mes en dos
   * años distintos salía «mismo día». Un acierto por accidente es un fallo con la
   * fecha puesta.
   */
  const mismoDia =
    fechaParaInput(a.absent_from) === fechaParaInput(a.absent_to);
  return mismoDia
    ? `No viene el ${fecha(a.absent_from)}`
    : `No viene del ${fecha(a.absent_from)} al ${fecha(a.absent_to)}`;
}

/* ── §P.2 · La asistencia, vista de través ────────────────────────────────── */

/**
 * Una fila de `GET /clubs/{club}/attendance`.
 *
 * ## Por qué esta consulta existe aparte del informe
 *
 * El informe de §P.9 da el agregado —«el martes a las 17:30 viene el 78 %»—; esto da
 * **las filas**. «Quien lleva la secretaría no pregunta por una clase: pregunta por
 * una semana», y abrir catorce sesiones para contestar «¿quién faltó el martes?» es
 * la clase de trabajo que hace que un club vuelva al cuaderno.
 *
 * ## `source` dice de quién es el dato
 *
 * «Una falta puesta por el sistema y una puesta por el maestro **no se discuten
 * igual**, y esa columna es la que evita la conversación.» Por eso viaja en la tabla
 * y no en un detalle: cuando una familia reclama, lo primero es saber quién lo marcó.
 */
export interface FilaDeAsistencia {
  readonly session_id: string;
  readonly session_date: Date | string;
  readonly starts_at: Date | string;
  readonly group_id: string;
  readonly group_name: string;
  readonly venue_name: string | null;
  readonly student_id: string;
  readonly student_name: string;
  readonly status: string;
  readonly minutes_late: number | null;
  readonly source: string;
}

export interface PaginaDeAsistencia {
  readonly data: readonly FilaDeAsistencia[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/**
 * Los cuatro estados, **copiados del DTO**: `present`, `absent`, `late`, `excused`.
 *
 * `excused` lleva color propio y no el de la falta: una ausencia avisada por la
 * familia no es lo mismo que no aparecer, y mezclarlas hace que el club llame a
 * alguien que ya avisó.
 */
export const ESTADOS_DE_ASISTENCIA: readonly {
  id: 'present' | 'absent' | 'late' | 'excused';
  etiqueta: string;
  clase: string;
}[] = [
  { id: 'present', etiqueta: 'Vino', clase: 'pastilla-bien' },
  { id: 'late', etiqueta: 'Tarde', clase: 'pastilla-espera' },
  { id: 'absent', etiqueta: 'Faltó', clase: 'pastilla-mal' },
  { id: 'excused', etiqueta: 'Avisó', clase: 'pastilla-paused' },
];

export const etiquetaDeAsistencia = (estado: string): string =>
  ESTADOS_DE_ASISTENCIA.find((e) => e.id === estado)?.etiqueta ?? estado;

export const claseDeAsistencia = (estado: string): string =>
  ESTADOS_DE_ASISTENCIA.find((e) => e.id === estado)?.clase ?? '';

/**
 * De dónde salió el dato. **Copiado del DTO**: `manual`, `checkin`, `import`, `auto`.
 *
 * `auto` es el que importa: lo puso el sistema, nadie lo miró. Es el único que se
 * dice en la tabla, porque los otros tres no cambian la conversación.
 */
export const etiquetaDeOrigen = (origen: string): string =>
  ({
    manual: 'lo puso el maestro',
    checkin: 'fichó con su tarjeta',
    import: 'vino en el Excel',
    auto: 'lo puso el sistema',
  })[origen] ?? origen;
